import { useState } from 'react';

export default function ShopSection() {
  const [selectedRank, setSelectedRank] = useState<string | null>(null);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [purchaseData, setPurchaseData] = useState({
    username: '',
    email: '',
    discord: '',
  });
  const [ibanCopied, setIbanCopied] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);

  const IBAN = 'TR45 0001 5001 5800 7287 0048 22';
  const ADMIN_EMAIL = 'furukonetwork@gmail.com';

  const ranks = [
    {
      name: 'VIP',
      price: '₺75',
      gradient: 'from-yellow-400 to-orange-500',
      features: [
        'Sohbette VIP Ön Eki',
        'VIP Komutlarına Erişim',
        '2x Günlük Ödüller',
        'Öncelikli Sunucu Erişimi',
        'Özel VIP Kiti',
      ],
      popular: false,
    },
    {
      name: 'CVIP',
      price: '₺140',
      gradient: 'from-cyan-400 to-blue-500',
      features: [
        'Sohbette CVIP Ön Eki',
        'Tüm VIP Özellikleri',
        '3x Günlük Ödüller',
        'Uçma Komutu',
        'Özel CVIP Kiti',
        'Özel Takma Ad Renkleri',
      ],
      popular: true,
    },
    {
      name: 'SPONSOR',
      price: '₺200',
      gradient: 'from-emerald-400 to-green-500',
      features: [
        'Sohbette SPONSOR Ön Eki',
        'Tüm CVIP Özellikleri',
        '5x Günlük Ödüller',
        'Işınlanma Komutları',
        'Özel SPONSOR Kiti',
        'Özel Sohbet Renkleri',
        'Öncelikli Destek',
      ],
      popular: false,
    },
    {
      name: 'FURUKOPARTNER',
      price: '₺500',
      gradient: 'from-purple-400 to-pink-500',
      features: [
        'Sohbette PARTNER Ön Eki',
        'Tüm SPONSOR Özellikleri',
        '10x Günlük Ödüller',
        'WorldEdit Erişimi',
        'Özel PARTNER Kiti',
        'Özel Parçacık Efektleri',
        'VIP Destek Kanalı',
        'Özel Partner Rozeti',
      ],
      popular: false,
    },
  ];

  const handlePurchase = (rankName: string) => {
    setSelectedRank(rankName);
    setShowPurchaseModal(true);
  };

  const closePurchaseModal = () => {
    setShowPurchaseModal(false);
    setSelectedRank(null);
    setPurchaseData({ username: '', email: '', discord: '' });
  };

  const closePaymentModal = () => {
    setShowPaymentModal(false);
    setIbanCopied(false);
  };

  const handleSubmitPurchase = (e: React.FormEvent) => {
    e.preventDefault();
    setShowPurchaseModal(false);
    setShowPaymentModal(true);
  };

  const copyIBAN = () => {
    navigator.clipboard.writeText(IBAN.replace(/\s/g, ''));
    setIbanCopied(true);
    setTimeout(() => setIbanCopied(false), 2000);
  };

  const handleConfirmPayment = async () => {
    setIsSubmitting(true);

    const selectedRankData = ranks.find(r => r.name === selectedRank);
    
    try {
      // Create URL-encoded form data
      const formBody = new URLSearchParams();
      formBody.append('email', ADMIN_EMAIL); // Send to admin email only
      formBody.append('subject', `Yeni ${selectedRank} Satın Alma - ${purchaseData.username}`);
      formBody.append('player_username', purchaseData.username);
      formBody.append('player_email', purchaseData.email);
      formBody.append('player_discord', purchaseData.discord);
      formBody.append('rank_purchased', selectedRank || '');
      formBody.append('rank_price', selectedRankData?.price || '');
      formBody.append('purchase_date', new Date().toLocaleString('tr-TR'));
      formBody.append('iban', IBAN);
      formBody.append('message', `${purchaseData.username} adlı oyuncu ${selectedRank} üyeliğini satın aldı.\n\nDetaylar:\n- Minecraft Kullanıcı Adı: ${purchaseData.username}\n- E-posta: ${purchaseData.email}\n- Discord: ${purchaseData.discord}\n- Rütbe: ${selectedRank}\n- Fiyat: ${selectedRankData?.price}\n- IBAN: ${IBAN}\n- Tarih: ${new Date().toLocaleString('tr-TR')}`);

      // Submit form with correct encoding
      const response = await fetch('https://readdy.ai/api/form/d5l1vh1ubuu1ag7leoog', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        alert(`Satın alma talebiniz alındı! ${purchaseData.username} kullanıcısı için ${selectedRank} rütbesi talebiniz yöneticiye iletildi. Ödeme kontrolü sonrası rütbeniz hesabınıza tanımlanacaktır.`);
        closePaymentModal();
        setPurchaseData({ username: '', email: '', discord: '' });
        setSelectedRank(null);
      } else {
        alert('Bir hata oluştu. Lütfen Discord üzerinden destek ekibimizle iletişime geçin.');
      }
    } catch (error) {
      console.error('Purchase error:', error);
      alert('Bir hata oluştu. Lütfen Discord üzerinden destek ekibimizle iletişime geçin.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <section id="shop" className="relative py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <div className="inline-block px-4 py-2 bg-purple-500/20 rounded-full mb-4">
              <span className="text-purple-400 font-bold text-sm uppercase tracking-wider">Sunucu Mağazası</span>
            </div>
            <h2 className="text-5xl md:text-6xl font-black text-white mb-4">
              Deneyiminizi Geliştirin
            </h2>
            <p className="text-xl text-gray-400 max-w-2xl mx-auto">
              Özel özellikleri ve avantajları açmak için mükemmel rütbeyi seçin
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {ranks.map((rank, index) => (
              <div
                key={index}
                className={`relative bg-white/5 backdrop-blur-sm rounded-2xl overflow-hidden border transition-all duration-300 hover:scale-105 hover:shadow-2xl ${
                  rank.popular ? 'border-cyan-400/50 shadow-cyan-500/20' : 'border-white/10'
                }`}
              >
                {rank.popular && (
                  <div className="absolute top-4 right-4 px-3 py-1 bg-cyan-400 text-black text-xs font-bold rounded-full z-10">
                    POPÜLER
                  </div>
                )}

                <div className={`bg-gradient-to-br ${rank.gradient} p-6 text-center`}>
                  <h3 className="text-2xl font-black text-white uppercase tracking-wider mb-2">{rank.name}</h3>
                  <p className="text-3xl font-black text-white">{rank.price}</p>
                </div>

                <div className="p-6">
                  <ul className="space-y-3 mb-6">
                    {rank.features.map((feature, idx) => (
                      <li key={idx} className="flex items-start gap-2 text-gray-300">
                        <i className="ri-check-line text-lime-400 text-lg flex-shrink-0 mt-0.5"></i>
                        <span className="text-sm">{feature}</span>
                      </li>
                    ))}
                  </ul>

                  <button
                    onClick={() => handlePurchase(rank.name)}
                    className={`w-full px-6 py-3 bg-gradient-to-r ${rank.gradient} hover:opacity-90 text-white rounded-full font-bold transition-all hover:scale-105 whitespace-nowrap cursor-pointer`}
                  >
                    Satın Al
                  </button>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-12 text-center">
            <p className="text-gray-400 mb-4">
              Satın alma işlemleri için Discord sunucumuzdan destek ekibimizle iletişime geçin
            </p>
            <a
              href="https://discord.gg/FurukoNetwork"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#5865F2] hover:bg-[#4752C4] text-white rounded-full font-semibold transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-discord-fill text-xl"></i>
              Discord'da Destek Al
            </a>
          </div>
        </div>
      </section>

      {/* Purchase Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative bg-gradient-to-br from-purple-900/90 to-black/90 backdrop-blur-md rounded-3xl p-8 max-w-md w-full border border-purple-500/30 shadow-2xl shadow-purple-500/20">
            <button
              onClick={closePurchaseModal}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center hover:bg-white/10 rounded-full transition-all cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-white"></i>
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-purple-400 to-pink-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-shopping-cart-2-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-black text-white mb-2">{selectedRank} Satın Al</h3>
              <p className="text-gray-400">Satın alma bilgilerinizi girin</p>
            </div>

            <form onSubmit={handleSubmitPurchase} className="space-y-4">
              <div>
                <label className="block text-white font-semibold mb-2">Minecraft Kullanıcı Adı</label>
                <input
                  type="text"
                  required
                  value={purchaseData.username}
                  onChange={(e) => setPurchaseData({ ...purchaseData, username: e.target.value })}
                  placeholder="Kullanıcı adınızı girin"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-all"
                />
              </div>

              <div>
                <label className="block text-white font-semibold mb-2">E-posta Adresi</label>
                <input
                  type="email"
                  required
                  value={purchaseData.email}
                  onChange={(e) => setPurchaseData({ ...purchaseData, email: e.target.value })}
                  placeholder="E-posta adresinizi girin"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-all"
                />
              </div>

              <div>
                <label className="block text-white font-semibold mb-2">Discord Kullanıcı Adı</label>
                <input
                  type="text"
                  required
                  value={purchaseData.discord}
                  onChange={(e) => setPurchaseData({ ...purchaseData, discord: e.target.value })}
                  placeholder="Discord#1234"
                  className="w-full px-4 py-3 bg-white/10 border border-white/20 rounded-lg text-white placeholder-gray-500 focus:outline-none focus:border-purple-400 transition-all"
                />
              </div>

              <div className="bg-purple-500/20 border border-purple-500/30 rounded-lg p-4">
                <p className="text-sm text-gray-300">
                  <i className="ri-information-fill text-purple-400 mr-2"></i>
                  Bir sonraki adımda IBAN bilgisi gösterilecektir.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closePurchaseModal}
                  className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-bold transition-all whitespace-nowrap cursor-pointer"
                >
                  İptal
                </button>
                <button
                  type="submit"
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-purple-400 to-pink-500 hover:opacity-90 text-white rounded-full font-bold transition-all hover:scale-105 whitespace-nowrap cursor-pointer"
                >
                  Devam Et
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Payment Modal */}
      {showPaymentModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative bg-gradient-to-br from-emerald-900/90 to-black/90 backdrop-blur-md rounded-3xl p-8 max-w-md w-full border border-emerald-500/30 shadow-2xl shadow-emerald-500/20">
            <button
              onClick={closePaymentModal}
              className="absolute top-4 right-4 w-10 h-10 flex items-center justify-center hover:bg-white/10 rounded-full transition-all cursor-pointer"
            >
              <i className="ri-close-line text-2xl text-white"></i>
            </button>

            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-gradient-to-br from-emerald-400 to-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <i className="ri-bank-card-fill text-3xl text-white"></i>
              </div>
              <h3 className="text-3xl font-black text-white mb-2">Ödeme Bilgileri</h3>
              <p className="text-gray-400">Aşağıdaki IBAN'a ödeme yapın</p>
            </div>

            <div className="space-y-6">
              <div className="bg-white/10 border border-white/20 rounded-lg p-6">
                <p className="text-sm text-gray-400 mb-2">IBAN Numarası</p>
                <div className="flex items-center justify-between gap-3">
                  <p className="text-xl font-mono font-bold text-white">{IBAN}</p>
                  <button
                    onClick={copyIBAN}
                    className="px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white rounded-lg font-semibold transition-all whitespace-nowrap cursor-pointer flex items-center gap-2"
                  >
                    <i className={`${ibanCopied ? 'ri-check-line' : 'ri-file-copy-line'}`}></i>
                    {ibanCopied ? 'Kopyalandı!' : 'Kopyala'}
                  </button>
                </div>
              </div>

              <div className="bg-emerald-500/20 border border-emerald-500/30 rounded-lg p-4">
                <h4 className="text-white font-bold mb-2 flex items-center gap-2">
                  <i className="ri-information-fill text-emerald-400"></i>
                  Önemli Bilgiler
                </h4>
                <ul className="text-sm text-gray-300 space-y-2">
                  <li>• Rütbe: <strong className="text-white">{selectedRank}</strong></li>
                  <li>• Tutar: <strong className="text-white">{ranks.find(r => r.name === selectedRank)?.price}</strong></li>
                  <li>• Kullanıcı: <strong className="text-white">{purchaseData.username}</strong></li>
                </ul>
              </div>

              <div className="bg-yellow-500/20 border border-yellow-500/30 rounded-lg p-4">
                <p className="text-sm text-gray-300">
                  <i className="ri-alert-fill text-yellow-400 mr-2"></i>
                  Ödeme yaptıktan sonra "Ödemeyi Onayla" butonuna tıklayın. Yönetici ekibimiz ödemenizi kontrol edip rütbenizi hesabınıza tanımlayacaktır.
                </p>
              </div>

              <div className="flex gap-3 pt-4">
                <button
                  type="button"
                  onClick={closePaymentModal}
                  className="flex-1 px-6 py-3 bg-white/10 hover:bg-white/20 text-white rounded-full font-bold transition-all whitespace-nowrap cursor-pointer"
                >
                  İptal
                </button>
                <button
                  onClick={handleConfirmPayment}
                  disabled={isSubmitting}
                  className="flex-1 px-6 py-3 bg-gradient-to-r from-emerald-400 to-green-500 hover:opacity-90 text-white rounded-full font-bold transition-all hover:scale-105 whitespace-nowrap cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Gönderiliyor...' : 'Ödemeyi Onayla'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Hidden form for email notification */}
      <form 
        id="purchase-notification-form" 
        data-readdy-form
        style={{ display: 'none' }}
        action="https://readdy.ai/api/form/d5l1t3lporqtj2nschv0"
        method="POST"
      >
        <input type="hidden" name="recipient_email" />
        <input type="hidden" name="player_username" />
        <input type="hidden" name="player_email" />
        <input type="hidden" name="player_discord" />
        <input type="hidden" name="rank_purchased" />
        <input type="hidden" name="rank_price" />
        <input type="hidden" name="purchase_date" />
        <input type="hidden" name="iban" />
        <input type="hidden" name="message" />
      </form>
    </>
  );
}
